// Direct imports
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";
import { TraceBehavior } from "./tracebehavior";
import { RuntimeToggleLatch, type RuntimeToggleable } from "./runtimeToggleable";
import { ViewAnchorData } from "../scenedata/behaviors/viewanchordata";
import type { SceneObject } from "../core/SceneObject";

// Shared across all ViewAnchor instances — safe because observers run synchronously.
const _tmpGoalPos = new Vector3();
const _tmpSelfPos = new Vector3();
const _tmpDelta = new Vector3();
const _tmpStep = new Vector3();
const _tmpFwd = new Vector3();
const _tmpRight = new Vector3();
const _tmpAnchorPos = new Vector3();
const _tmpToGoal = new Vector3();
const _tmpToSelf = new Vector3();
const _tmpLocalAxis = new Vector3();
const _tmpQuat = new Quaternion();

/** Comfort slack as a fraction of the goal's HORIZONTAL offset: the user's
 *  horizontal distance leaving [1 − REST_BAND, 1 + REST_BAND] × that offset
 *  re-engages a glide (walk-away catches up, lean-in retreats). Fixed
 *  internal constant (not authored in ViewAnchorData). */
const REST_BAND = 0.25;

/** Degrees of gaze tilt the goal frame follows. 0 would keep the frame at
 *  eye height; 90 would follow the full gaze ray (a steep look-down drags
 *  the goal toward the floor). 30 tilts gently with the gaze without ever
 *  diving. Fixed internal constant (not authored in ViewAnchorData). */
const PITCH_COUPLING_DEGREES = 30;

/** The object counts as "arrived" within this distance of the goal. */
const VIEW_ARRIVE_EPSILON = 0.05;

/**
 * ViewAnchor behavior - keeps an object anchored in the user's view.
 *
 * Seeks a point offset in the gaze frame of the user (the active camera) or
 * an anchor object: `forward` meters along the gaze, `lateral` to the
 * right, `vertical` world-up. The frame follows gaze tilt only up to
 * PITCH_COUPLING_DEGREES; the lateral axis stays horizontal so tilt never
 * rolls the offset. One shape covers summoned modals (forward only),
 * side-docked panels (lateral), and toasts (vertical). An angular dead zone
 * (`viewAngle`, measured object-to-goal at the anchor) ignores small head
 * turns; drifting past it, or the user's distance leaving the REST_BAND
 * comfort band, re-engages a glide (honoring `delay`). Walking into the
 * object pushes its goal back, so it retreats gracefully.
 *
 * `axes` constrains movement to world axes (["x","z"] holds the authored
 * height).
 *
 * ViewAnchor moves position only. To face while anchored, compose with a
 * Billboard behavior — behaviors stack by design.
 *
 * RuntimeToggleable: the graph can switch anchoring on/off at play time
 * (trace/setBehaviorEnabled). Disabling stops the object where it is;
 * re-enabling re-runs `delay` (notice, then recenter). The authored
 * `enabled` field is the state at every play-mode start.
 */
export class ViewAnchor extends TraceBehavior<ViewAnchorData> implements RuntimeToggleable {
    static readonly behaviorType = "ViewAnchor" as const;

    private _observer: Observer<Scene> | null = null;
    private _lastFrameTime: number | null = null;
    private _outOfRangeSince: number | null = null;
    private _anchorObj: SceneObject | null = null;
    /** Runtime on/off + the Scene-Starts play-start latch (see RuntimeToggleLatch). */
    private readonly _toggle = new RuntimeToggleLatch();
    /** Currently gliding toward the goal (hysteresis — a glide runs to
     *  arrival even if the trigger condition clears mid-flight). */
    private _engaged = false;

    setRuntimeEnabled(enabled: boolean): void {
        if (this._toggle.set(enabled, this._observer !== null)) {
            // Fresh clock + delay on every toggle: re-enable "notices, then
            // recenters"; a stale dt can't produce a jump either way.
            this._lastFrameTime = null;
            this._outOfRangeSince = null;
            this._engaged = false;
        }
    }

    isRuntimeEnabled(): boolean {
        return this._toggle.enabled;
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            this._applyViewAnchor();
        } else {
            this._removeViewAnchor();
        }
    }

    protected onDetach(): void {
        this._removeViewAnchor();
    }

    protected async applyCommit(_patch: Partial<ViewAnchorData>): Promise<void> {
        this._removeViewAnchor();
        if (this.playMode) {
            this._applyViewAnchor();
        }
    }

    /** Resolve the anchor object, cached until disposed. Null = camera anchor. */
    private _resolveAnchorObject(): SceneObject | null {
        const path = this._resolved.target ?? "";
        if (!path) return null;
        if (!this._anchorObj || this._anchorObj.transform.isDisposed()) {
            this._anchorObj = this.sceneObject.traceScene.resolveSceneObject(path, this.sceneObject);
        }
        return this._anchorObj;
    }

    /**
     * Resolve the anchor: position + the gaze frame (unit forward and unit
     * horizontal right). Camera anchors use the gaze ray; object anchors use
     * their world +Z. The forward follows gaze tilt only up to
     * ±PITCH_COUPLING_DEGREES; the right axis is horizontal always (gaze
     * tilt never rolls the offset). Returns false when unavailable or the
     * gaze is degenerate (straight up/down — no heading to build from).
     */
    private _resolveViewAnchor(outPos: Vector3, outFwd: Vector3, outRight: Vector3): boolean {
        const path = this._resolved.target ?? "";
        const scene = this.sceneObject.babylonScene;
        if (!path) {
            const camera = scene.activeCamera;
            if (!camera) return false;
            outPos.copyFrom(camera.globalPosition);
            // Camera forward via the world matrix (getForwardRay would pull in
            // the Ray side-effect import); local forward is -Z in RH scenes
            _tmpLocalAxis.set(0, 0, scene.useRightHandedSystem ? -1 : 1);
            camera.getDirectionToRef(_tmpLocalAxis, outFwd);
        } else {
            const obj = this._resolveAnchorObject();
            // Reject self AND the whole own subtree: anchoring to your own
            // descendant is unbounded runaway drift — moving self moves the
            // anchor, leaving a constant delta to chase every frame.
            if (!obj || obj === this.sceneObject
                || obj.transform.isDescendantOf(this.sceneObject.transform)) return false;
            outPos.copyFrom(obj.transform.getAbsolutePosition());
            obj.transform.getWorldMatrix().decompose(undefined, _tmpQuat, undefined);
            outFwd.set(0, 0, 1);
            outFwd.applyRotationQuaternionToRef(_tmpQuat, outFwd);
        }
        const hLen = Math.sqrt(outFwd.x * outFwd.x + outFwd.z * outFwd.z);
        if (hLen < 0.05) return false; // looking straight down/up — hold position
        const ux = outFwd.x / hLen;
        const uz = outFwd.z / hLen;

        // Tilt the frame with the gaze, clamped to ±PITCH_COUPLING_DEGREES
        const coupling = PITCH_COUPLING_DEGREES * (Math.PI / 180);
        const pitch = Math.max(-coupling, Math.min(coupling, Math.atan2(outFwd.y, hLen)));
        const cosP = Math.cos(pitch);
        outFwd.set(ux * cosP, Math.sin(pitch), uz * cosP);

        // Horizontal right of the heading, handedness-aware
        if (scene.useRightHandedSystem) {
            outRight.set(-uz, 0, ux);
        } else {
            outRight.set(uz, 0, -ux);
        }
        return true;
    }

    private _applyViewAnchor(): void {
        const scene = this.sceneObject.babylonScene;
        this._lastFrameTime = null;
        this._outOfRangeSince = null;
        this._anchorObj = null;
        this._engaged = false;
        // Every play start resets the runtime toggle to the authored state —
        // unless an enable already landed this play-start window (Scene
        // Starts trigger), which the reset consumes rather than stomps
        this._toggle.reset(this._resolved.enabled ?? true);

        this._observer = scene.onBeforeRenderObservable.add(() => {
            if (!this._toggle.enabled) return;

            const r = this._resolved;
            const speed = Math.max(0, r.speed ?? 1.5);
            if (speed === 0) return;

            // Goal = anchor + forward × gaze + lateral × right + vertical
            // world-up.
            if (!this._resolveViewAnchor(_tmpAnchorPos, _tmpFwd, _tmpRight)) return;
            const forward = r.forward ?? 1;
            const lateral = r.lateral ?? 0;
            _tmpGoalPos.copyFrom(_tmpAnchorPos);
            _tmpGoalPos.x += _tmpFwd.x * forward + _tmpRight.x * lateral;
            _tmpGoalPos.y += _tmpFwd.y * forward + (r.vertical ?? 0);
            _tmpGoalPos.z += _tmpFwd.z * forward + _tmpRight.z * lateral;

            const transform = this.sceneObject.transform;
            transform.computeWorldMatrix(true);
            _tmpSelfPos.copyFrom(transform.getAbsolutePosition());

            // Movement delta constrained to the allowed world axes — arrival
            // is measured in the same masked space, so a height-locked panel
            // doesn't endlessly chase a vertical gap.
            _tmpDelta.copyFrom(_tmpGoalPos).subtractInPlace(_tmpSelfPos);
            const axes = r.axes;
            if (axes) {
                if (!axes.includes("x")) _tmpDelta.x = 0;
                if (!axes.includes("y")) _tmpDelta.y = 0;
                if (!axes.includes("z")) _tmpDelta.z = 0;
            }
            const dist = _tmpDelta.length();

            // Wall-clock dt (framerate-independent, 100 ms hitch clamp)
            const now = performance.now();
            const dt = this._lastFrameTime === null
                ? 0
                : Math.min((now - this._lastFrameTime) / 1000, 0.1);
            this._lastFrameTime = now;

            if (!this._updateViewEngagement(now, dist, _tmpGoalPos, _tmpAnchorPos, _tmpSelfPos)) return;
            if (dt === 0 || dist < 1e-6) return;

            // The goal IS the resting point — glide the full delta, eased
            // with a hard speed cap
            const damping = r.damping ?? 0.25;
            _tmpStep.copyFrom(_tmpDelta);
            if (damping > 0) {
                _tmpStep.scaleInPlace(1 - Math.exp(-dt / damping));
            }
            const maxStep = speed * dt;
            const stepLen = _tmpStep.length();
            if (stepLen > maxStep) {
                _tmpStep.scaleInPlace(maxStep / stepLen);
            }

            _tmpSelfPos.addInPlace(_tmpStep);
            transform.setAbsolutePosition(_tmpSelfPos);
        });
    }

    /**
     * Engagement with hysteresis. A glide starts when the object sits more
     * than `viewAngle` off its GOAL as seen from the anchor (drift — full
     * 3D, so vertical drift recenters like horizontal; and measured to the
     * goal, never the gaze axis, so off-axis goals keep their whole dead
     * zone), or when the anchor's HORIZONTAL distance to the object leaves
     * the REST_BAND comfort band around the goal's horizontal offset
     * (walk-away / lean-in — horizontal so crouching or standing can't trip
     * it, and anchor-relative so turning the head alone can't either,
     * load-bearing for viewAngle 180 = drift-ignoring). Once engaged,
     * the glide runs to arrival even if the trigger clears mid-flight. The
     * engage transition honors `delay`. Returns true to move.
     *
     * @param now       - Wall-clock time in ms (performance.now()).
     * @param distToGoal - Axis-masked distance from the object to its goal.
     * @param goalPos   - The goal position in world space.
     * @param anchorPos - The anchor position in world space.
     * @param selfPos   - The object's current world position.
     */
    private _updateViewEngagement(
        now: number,
        distToGoal: number,
        goalPos: Vector3,
        anchorPos: Vector3,
        selfPos: Vector3,
    ): boolean {
        const r = this._resolved;

        _tmpToGoal.copyFrom(goalPos).subtractInPlace(anchorPos);
        const restDist = _tmpToGoal.length();

        if (this._engaged) {
            if (distToGoal <= Math.max(VIEW_ARRIVE_EPSILON, restDist * 0.02)) {
                this._engaged = false; // arrived — rest until re-triggered
                this._outOfRangeSince = null;
                return false;
            }
            return true;
        }

        _tmpToSelf.copyFrom(selfPos).subtractInPlace(anchorPos);

        // Drift: angle between the object and its goal, at the anchor
        const selfDist = _tmpToSelf.length();
        let angleDeg = 0;
        if (selfDist > 1e-4 && restDist > 1e-4) {
            const cos = Math.min(1, Math.max(-1,
                Vector3.Dot(_tmpToSelf, _tmpToGoal) / (selfDist * restDist)));
            angleDeg = (Math.acos(cos) * 180) / Math.PI;
        }

        // Comfort band: horizontal distances only
        const radial = Math.sqrt(_tmpToSelf.x * _tmpToSelf.x + _tmpToSelf.z * _tmpToSelf.z);
        const radialRest = Math.sqrt(_tmpToGoal.x * _tmpToGoal.x + _tmpToGoal.z * _tmpToGoal.z);
        const tooFar = radialRest > 1e-4 && radial > radialRest * (1 + REST_BAND); // walked away
        const tooClose = radialRest > 1e-4 && radial < radialRest * (1 - REST_BAND); // leaning in

        const viewAngle = r.viewAngle ?? 20;
        const wantEngage = angleDeg > viewAngle || tooFar || tooClose;
        if (!wantEngage) {
            this._outOfRangeSince = null;
            return false;
        }

        // Notice, then recenter — the delay is the "notice" beat
        if (this._outOfRangeSince === null) this._outOfRangeSince = now;
        const delay = (r.delay ?? 0) * 1000;
        if (now - this._outOfRangeSince < delay) return false;

        this._engaged = true;
        return true;
    }

    private _removeViewAnchor(): void {
        if (this._observer) {
            this.sceneObject.babylonScene.onBeforeRenderObservable.remove(this._observer);
            this._observer = null;
        }
        this._lastFrameTime = null;
        this._outOfRangeSince = null;
        this._anchorObj = null;
        this._engaged = false;
        this._toggle.clearPending();
    }
}
